import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LocalizedText } from '@cr/shared';
import { api } from '@/lib/api';
import { localized } from '@/lib/format';
import { useSession } from '@/lib/session';
import { haptic } from '@/lib/telegram';
import Icon from '@/components/icon';
import { ProductGrid, type ProductSummary } from '@/components/product-card';
import {
  Button,
  CardSkeleton,
  Chip,
  EmptyState,
  ErrorState,
  Screen,
  Select,
  SubHeader,
} from '@/components/ui';

interface CategoryNode {
  id: string;
  level: number;
  name: LocalizedText;
  children: CategoryNode[];
}

interface Filters {
  sizeGroups: Array<{
    id: string;
    code: string;
    name: LocalizedText;
    sizes: Array<{ id: string; value: string }>;
  }>;
  colors: Array<{ id: string; name: LocalizedText; hex: string }>;
  brands: Array<{ id: string; name: string }>;
}

interface ProductList {
  items: ProductSummary[];
  total: number;
  page: number;
  pages: number;
}

export default function CatalogPage() {
  const { t } = useTranslation();
  const { authorized } = useSession();
  const [params, setParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  const q = params.get('q') ?? '';
  const categoryId = params.get('categoryId') ?? '';
  const sizes = params.get('sizes') ?? '';
  const colors = params.get('colors') ?? '';
  const brands = params.get('brands') ?? '';
  const sort = params.get('sort') ?? 'new';

  const patch = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    setParams(merged, { replace: true });
  };

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/catalog/categories')).data,
  });

  const filters = useQuery({
    queryKey: ['filters'],
    queryFn: async () => (await api.get<Filters>('/catalog/filters')).data,
  });

  // Бесконечный список: «показать ещё» дописывает страницу, а не заменяет её
  const products = useInfiniteQuery({
    queryKey: ['products', 'catalog', q, categoryId, sizes, colors, brands, sort],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<ProductList>('/catalog/products', {
          params: {
            q: q || undefined,
            categoryId: categoryId || undefined,
            sizes: sizes || undefined,
            colors: colors || undefined,
            brands: brands || undefined,
            sort,
            page: pageParam,
            limit: 20,
          },
        })
      ).data,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });

  const loaded = products.data?.pages.flatMap((p) => p.items) ?? [];
  const total = products.data?.pages[0]?.total ?? 0;

  const favorites = useQuery({
    queryKey: ['favorites'],
    queryFn: async () => (await api.get<Array<{ id: string }>>('/favorites')).data,
    enabled: authorized,
  });

  const favoriteIds = new Set((favorites.data ?? []).map((f) => f.id));
  const flat = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.flatMap((n) => [n, ...flat(n.children)]);

  const toggleIn = (list: string, id: string) => {
    const set = new Set(list.split(',').filter(Boolean));
    if (set.has(id)) set.delete(id);
    else set.add(id);
    return [...set].join(',');
  };

  const activeFilters = [sizes, colors, brands].filter(Boolean).length;

  return (
    <>
      <SubHeader title={t('catalog.title')} />
      <Screen>
        <input
          value={q}
          onChange={(e) => patch({ q: e.target.value || null })}
          placeholder={t('home.searchPlaceholder')}
          className="w-full rounded-xl bg-surface px-4 py-3 text-sm shadow-soft outline-none placeholder:text-muted"
        />

        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
          <Chip active={!categoryId} onClick={() => patch({ categoryId: null })}>
            {t('catalog.allCategories')}
          </Chip>
          {flat(categories.data ?? []).map((category) => (
            <Chip
              key={category.id}
              active={categoryId === category.id}
              onClick={() => {
                haptic.select();
                patch({ categoryId: category.id });
              }}
            >
              {localized(category.name)}
            </Chip>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="flex items-center gap-1.5 rounded-xl bg-surface px-3 py-2 text-sm shadow-soft"
          >
            <Icon name="filter" size={16} className="text-bordo" />
            {t('catalog.filters')}
            {activeFilters > 0 && ` · ${activeFilters}`}
          </button>

          <Select
            value={sort}
            onChange={(next) => patch({ sort: next })}
            options={[
              { value: 'new', label: t('catalog.sortNew') },
              { value: 'popular', label: t('catalog.sortPopular') },
              { value: 'price_asc', label: t('catalog.sortPriceAsc') },
              { value: 'price_desc', label: t('catalog.sortPriceDesc') },
            ]}
          />
        </div>

        {showFilters && filters.data && (
          <div className="space-y-4 rounded-card bg-surface p-4 shadow-soft">
            {filters.data.sizeGroups
              .filter((group) => group.sizes.length > 0)
              .map((group) => (
                <div key={group.id} className="space-y-2">
                  <p className="text-xs uppercase text-muted">
                    {t('catalog.size')} · {localized(group.name)}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.sizes.map((size) => (
                      <button
                        key={size.id}
                        onClick={() => patch({ sizes: toggleIn(sizes, size.id) || null })}
                        className={`rounded-lg px-2.5 py-1 text-sm ${
                          sizes.includes(size.id) ? 'bg-bordo text-white' : 'bg-cream'
                        }`}
                      >
                        {size.value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

            <div className="space-y-2">
              <p className="text-xs uppercase text-muted">{t('catalog.color')}</p>
              <div className="flex flex-wrap gap-2">
                {filters.data.colors.map((color) => (
                  <button
                    key={color.id}
                    onClick={() => patch({ colors: toggleIn(colors, color.id) || null })}
                    aria-label={localized(color.name)}
                    className={`size-8 rounded-full border-2 ${
                      colors.includes(color.id) ? 'border-bordo' : 'border-line'
                    }`}
                    style={{ background: color.hex }}
                  />
                ))}
              </div>
            </div>

            {filters.data.brands.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase text-muted">{t('product.brand')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {filters.data.brands.map((brand) => (
                    <button
                      key={brand.id}
                      onClick={() => patch({ brands: toggleIn(brands, brand.id) || null })}
                      className={`rounded-lg px-2.5 py-1 text-sm ${
                        brands.includes(brand.id) ? 'bg-bordo text-white' : 'bg-cream'
                      }`}
                    >
                      {brand.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              onClick={() => patch({ sizes: null, colors: null, brands: null })}
            >
              {t('catalog.reset')}
            </Button>
          </div>
        )}

        {products.isError ? (
          <ErrorState onRetry={() => void products.refetch()} />
        ) : products.isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {[0, 1, 2, 3].map((i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : loaded.length === 0 ? (
          <EmptyState title={t('catalog.nothingFound')} />
        ) : (
          <>
            <p className="text-sm text-muted">{t('catalog.found', { count: total })}</p>
            <ProductGrid products={loaded} favorites={favoriteIds} />

            {products.hasNextPage && (
              <Button
                variant="secondary"
                className="w-full"
                disabled={products.isFetchingNextPage}
                onClick={() => void products.fetchNextPage()}
              >
                {products.isFetchingNextPage ? t('common.loading') : t('catalog.loadMore')}
              </Button>
            )}
          </>
        )}
      </Screen>
    </>
  );
}
